<template>
  <button
    :class="[
      'owc-header-button__iconWrapper',
      { 'owc-header-button--flyout-open': open, 'owc-header-button__iconWrapper--light': lightMode },
    ]"
    :aria-label="displayLabel"
  >
    <wb-icon
      :name="displayIcon"
      :class="[
        `owc-header-button__icon${isIconSmall ? '--small' : ''}`,
        { 'owc-header-button__badge': shouldDisplayBadge },
      ]"
    ></wb-icon>
  </button>
</template>

<script lang="ts">
import Vue from 'vue';
import { HeaderButtonData, HeaderButtonComputed, HeaderButtonProps } from './HeaderButton';
import { HeaderButtonChangeEvent } from '@owc-utils/Seamless/headerIntegrations.d';
import { HeaderEvents } from '@owc-utils/Seamless/headerEvents.enum';

export default Vue.extend<HeaderButtonData, {}, HeaderButtonComputed, HeaderButtonProps>({
  name: 'HeaderButton',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      required: false,
    },
    isIconSmall: {
      type: Boolean,
      default: true,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    badge: {
      type: [String, Number],
      required: false,
    },
    lightMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      eventIcon: null,
      eventLabel: null,
      eventBadge: null,
    };
  },
  mounted() {
    this.$el?.addEventListener(
      HeaderEvents.HEADER_ICON_CHANGE_EVENT,
      ({ detail }: CustomEvent<HeaderButtonChangeEvent>) => {
        if (detail.icon) {
          this.eventIcon = detail.icon;
        }
        if (detail.label) {
          this.eventLabel = detail.label;
        }
        if (detail.badge) {
          this.eventBadge = detail.badge;
        }
      },
    );
  },
  computed: {
    displayIcon() {
      return this.eventIcon || this.icon;
    },
    displayLabel() {
      return this.eventLabel || this.ariaLabel;
    },
    displayBadge() {
      return this.eventBadge || this.badge;
    },
    shouldDisplayBadge() {
      const counter = this.displayBadge;

      if (counter) {
        return Number(counter) >= 0;
      }

      return false;
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';
@import '@owc-packages/Header/src/styles/flyout-vars';

.owc-header-button {
  &__iconWrapper {
    @extend %header-icon-button;
    position: relative;

    &:after {
      position: absolute;

      top: calc(100% + var(--wb-spacing-xxs));
      left: calc(50% - var(--wb-spacing-xxs));

      width: 0;
      height: 0;

      border-right: solid 9px transparent;
      border-bottom: solid 9px var(--wb-white);
      border-left: solid 9px transparent;

      transform: translateY($flyout-animation-translate);
      opacity: 0;

      content: '';

      @include breakpoint-to(mq2) {
        content: none;
      }
    }
    &:hover {
      background-color: var(--wb-grey-15);
    }
    &--light {
      @include breakpoint-from(mq3) {
        &:hover {
          background-color: var(--wb-white); //Enforces no hover on the flyout
        }
      }
    }
  }
  &__icon {
    width: $button-inner-icon-size--large;
    height: $button-inner-icon-size--large;
  }
  &__icon--small {
    width: $button-inner-icon-size--small;
    height: $button-inner-icon-size--small;
  }
  &__badge {
    &:before {
      position: absolute;
      top: var(--wb-spacing-xxs);
      right: var(--wb-spacing-xxs);
      z-index: $z-index-content-lvl1;

      width: var(--wb-spacing-xxs);
      height: var(--wb-spacing-xxs);

      border-radius: 50%;

      background-color: var(--wb-blue-50);

      content: '';
    }
  }
  &--flyout-open {
    background-color: var(--wb-grey-15);

    &:after {
      transform: translateY(0);
      opacity: 1;

      transition-timing-function: $flyout-animation-timing;
      transition-duration: $flyout-animation-time;
      transition-property: opacity, transform;
    }
  }

  @include rtl-rule('.owc-header-button__icon[name="bds/chevron-right/16"]', false) {
    transform: rotate(180deg);
  }
  @include rtl-rule('.owc-header-button__icon[name="bds/chevron-left/24"]', false) {
    transform: rotate(180deg);
  }
}
</style>
